import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCompare } from '@/contexts/CompareContext';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, FileText, Eye, GitCompare, Calendar, DollarSign, Shield, Trash2, TrendingUp } from 'lucide-react';

const MyPoliciesPage = () => {
  const { user } = useAuth();
  const { addToCompare } = useCompare();
  const navigate = useNavigate();
  
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [quoteRequestsCount, setQuoteRequestsCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchPolicies();
      fetchQuoteRequestsCount();
    } else {
      setLoading(false);
    }
  }, [user, filterType]);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const params = filterType !== 'all' ? { policy_type: filterType } : {};
      const response = await api.get('/user-policies', { params });
      setPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuoteRequestsCount = async () => {
    try {
      const response = await api.get('/quote-requests/count');
      setQuoteRequestsCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch quote requests count:', error);
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      await api.delete(`/user-policies/${policyId}`);
      toast.success('Policy deleted successfully');
      fetchPolicies();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error('Failed to delete policy');
    }
  };

  const handleCompare = (policy) => {
    // Add user policy to comparison with isUserPolicy flag
    addToCompare(policy, true);
    
    // Track quote request
    api.post('/quote-requests', {
      policy_type: policy.policy_type,
      user_policy_id: policy.id,
      marketplace_policy_ids: []
    }).catch(error => console.error('Failed to track quote request:', error));
    
    // Navigate to filtered policies page
    navigate(`/policies?type=${policy.policy_type}`);
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpiringSoon = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 && days <= 30;
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  const getPolicyIcon = (type) => {
    switch (type) {
      case 'Health':
        return Shield;
      case 'Car':
      case 'Bike':
        return FileText;
      default:
        return FileText;
    }
  };

  // Calculate statistics
  const activePolicies = policies.filter(p => !isExpired(p.expiry_date));
  const totalCoverage = policies.reduce((sum, p) => sum + (p.premium || 0), 0);
  
  // Coverage by category
  const coverageByCategory = policies.reduce((acc, policy) => {
    const type = policy.policy_type;
    if (!acc[type]) {
      acc[type] = { count: 0, premium: 0 };
    }
    acc[type].count += 1;
    acc[type].premium += policy.premium || 0;
    return acc;
  }, {});

  const categoryData = Object.entries(coverageByCategory).map(([type, data]) => ({
    type,
    count: data.count,
    premium: data.premium,
    percentage: totalCoverage > 0 ? ((data.premium / totalCoverage) * 100).toFixed(1) : 0
  }));

  if (!user) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-secondary-600 mb-4">Please login to view your policies</p>
            <Button onClick={() => navigate('/login')}>Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="my-policies-page">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold font-heading text-secondary-900 mb-2">
                My Policies
              </h1>
              <p className="text-lg text-secondary-600">
                Manage and compare your existing insurance policies
              </p>
            </div>
            <Link to="/add-policy">
              <Button size="lg" data-testid="add-policy-button">
                <Plus className="mr-2 h-5 w-5" />
                Add Policy
              </Button>
            </Link>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-secondary-700">Filter by type:</label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48" data-testid="filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Health">Health</SelectItem>
                <SelectItem value="Car">Car</SelectItem>
                <SelectItem value="Bike">Bike</SelectItem>
                <SelectItem value="Life">Life</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
        {/* Dashboard Cards - Only show when user has policies */}
        {!loading && policies.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Active Policies Card */}
            <Card className="border-2 border-primary-200 bg-gradient-to-br from-primary-50 to-blue-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                </div>
                <p className="text-sm font-medium text-secondary-600 mb-1">Active Policies</p>
                <p className="text-3xl font-bold text-primary-700 font-mono">{activePolicies.length}</p>
                <p className="text-xs text-secondary-500 mt-1">
                  of {policies.length} total
                </p>
              </CardContent>
            </Card>

            {/* Total Coverage Card */}
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                </div>
                <p className="text-sm font-medium text-secondary-600 mb-1">Total Annual Premium</p>
                <p className="text-2xl font-bold text-green-700 font-mono">
                  {formatCurrency(totalCoverage)}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  across all policies
                </p>
              </CardContent>
            </Card>

            {/* Coverage by Category Card */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                </div>
                <p className="text-sm font-medium text-secondary-600 mb-1">Policy Types</p>
                <p className="text-3xl font-bold text-purple-700 font-mono">
                  {Object.keys(coverageByCategory).length}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  {Object.keys(coverageByCategory).join(', ')}
                </p>
              </CardContent>
            </Card>

            {/* Quote Requests Card */}
            <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-yellow-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                    <GitCompare className="h-6 w-6 text-white" />
                  </div>
                </div>
                <p className="text-sm font-medium text-secondary-600 mb-1">Quote Requests</p>
                <p className="text-3xl font-bold text-orange-700 font-mono">{quoteRequestsCount}</p>
                <p className="text-xs text-secondary-500 mt-1">
                  comparisons initiated
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Coverage Breakdown - Only show when user has multiple policy types */}
        {!loading && policies.length > 0 && categoryData.length > 1 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-600" />
                Coverage Breakdown by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {categoryData.map((item) => {
                  const Icon = getPolicyIcon(item.type);
                  return (
                    <div key={item.type} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Icon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-secondary-900">{item.type} Insurance</p>
                          <p className="text-sm text-secondary-600">
                            {item.count} {item.count === 1 ? 'policy' : 'policies'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg text-primary-700 font-mono">
                            {formatCurrency(item.premium)}
                          </p>
                          <p className="text-xs text-secondary-500">{item.percentage}% of total</p>
                        </div>
                        <div className="w-16">
                          <div className="h-2 bg-secondary-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary-600 rounded-full"
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : policies.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-10 w-10 text-secondary-400" />
              </div>
              <h3 className="text-2xl font-semibold text-secondary-900 mb-2">No policies added yet</h3>
              <p className="text-base text-secondary-600 mb-6">
                Add your existing insurance policies to compare them with other available plans
              </p>
              <Link to="/add-policy">
                <Button size="lg" data-testid="add-first-policy-button">
                  <Plus className="mr-2 h-5 w-5" />
                  Add Your First Policy
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {policies.map((policy) => {
              const Icon = getPolicyIcon(policy.policy_type);
              const expired = isExpired(policy.expiry_date);
              const expiringSoon = !expired && isExpiringSoon(policy.expiry_date);
              
              return (
                <Card
                  key={policy.id}
                  className="hover:shadow-lg transition-all duration-200 border-2 border-secondary-200 hover:border-primary-400"
                  data-testid={`policy-card-${policy.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Icon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <Badge variant="secondary" className="text-xs mb-1">
                            {policy.policy_type}
                          </Badge>
                        </div>
                      </div>
                      {expired && (
                        <Badge variant="destructive" className="text-xs">Expired</Badge>
                      )}
                      {expiringSoon && (
                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">
                          Expiring Soon
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl font-semibold text-secondary-900">
                      {policy.insurer_name}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Policy No: {policy.policy_number}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Dates */}
                    <div className="space-y-2 pb-4 border-b border-secondary-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-secondary-600 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Start Date
                        </span>
                        <span className="font-medium text-secondary-900">
                          {formatDate(policy.start_date)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-secondary-600 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Expiry Date
                        </span>
                        <span className={`font-medium ${expired ? 'text-red-600' : expiringSoon ? 'text-orange-600' : 'text-secondary-900'}`}>
                          {formatDate(policy.expiry_date)}
                        </span>
                      </div>
                    </div>

                    {/* Premium */}
                    {policy.premium && (
                      <div className="flex items-center justify-between py-2 bg-primary-50 rounded-lg px-3">
                        <span className="text-sm text-secondary-600 flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          Annual Premium
                        </span>
                        <span className="font-bold text-lg font-mono text-primary-700">
                          {formatCurrency(policy.premium)}
                        </span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Link to={`/my-policies/${policy.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full" data-testid={`view-details-${policy.id}`}>
                          <Eye className="mr-1 h-4 w-4" />
                          View Details
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => handleCompare(policy)}
                        data-testid={`compare-policy-${policy.id}`}
                      >
                        <GitCompare className="mr-1 h-4 w-4" />
                        Compare
                      </Button>
                    </div>

                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(policy.id)}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-policy-${policy.id}`}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete Policy
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPoliciesPage;
